import { ConfirmDialog } from '@/shared/components';
import type { PropertyFileTreeNode } from '@/shared/api';
import { NewFolderDialog } from './NewFolderDialog';
import { RenameFolderDialog } from './RenameFolderDialog';
import { MoveFileDialog } from './MoveFileDialog';
import { ReclassifyDialog } from './ReclassifyDialog';
import { segmentsOf } from './akteFolderRules';
import type { useAkteDialogs } from './useAkteDialogs';
import type { useAkteMutations } from './useAkteMutations';

interface Props {
  tree: PropertyFileTreeNode;
  dialogs: ReturnType<typeof useAkteDialogs>;
  mutations: ReturnType<typeof useAkteMutations>;
}

/** Rendert alle Mutations-Dialoge (Move/Reclassify/Ordner-CRUD), gespeist aus useAkteDialogs. */
export function AkteMutationDialogs({ tree, dialogs, mutations }: Props) {
  return (
    <>
      {dialogs.moveFile && (
        <MoveFileDialog
          open
          onOpenChange={(v) => !v && dialogs.closeMove()}
          file={dialogs.moveFile}
          tree={tree}
          onMove={(target) => mutations.move(dialogs.moveFile!, target)}
          onCreateSubfolder={() => {
            const parent = dialogs.moveFile!.folder;
            dialogs.closeMove();
            dialogs.openNewFolder(parent);
          }}
        />
      )}

      {dialogs.reclassifyFile && (
        <ReclassifyDialog
          open
          onOpenChange={(v) => !v && dialogs.closeReclassify()}
          file={dialogs.reclassifyFile}
          suggestedKind={dialogs.reclassifyKind}
          onReclassify={(kind, issuedOn) =>
            mutations.reclassify(dialogs.reclassifyFile!, kind, issuedOn)
          }
        />
      )}

      {dialogs.newFolderParent !== null && (
        <NewFolderDialog
          open
          onOpenChange={(v) => !v && dialogs.closeNewFolder()}
          tree={tree}
          defaultParent={dialogs.newFolderParent}
          onCreate={mutations.createFolder}
        />
      )}

      {dialogs.renameFolder !== null && (
        <RenameFolderDialog
          open
          onOpenChange={(v) => !v && dialogs.closeRenameFolder()}
          folderPath={dialogs.renameFolder}
          onRename={(newName) => mutations.renameFolder(dialogs.renameFolder!, newName)}
        />
      )}

      {dialogs.deleteFileTarget && (
        <ConfirmDialog
          open
          onOpenChange={(v) => !v && dialogs.closeDeleteFile()}
          title="In den Papierkorb verschieben?"
          description="Die Datei bleibt 30 Tage im Papierkorb und kann wiederhergestellt werden."
          confirmLabel="In den Papierkorb"
          onConfirm={() => {
            void mutations.deleteFile(dialogs.deleteFileTarget!);
            dialogs.closeDeleteFile();
          }}
        />
      )}

      {dialogs.deleteFolder && (
        <ConfirmDialog
          open
          onOpenChange={(v) => !v && dialogs.closeDeleteFolder()}
          title={`Ordner ${segmentsOf(dialogs.deleteFolder).pop()} löschen?`}
          description="Nur leere Ordner können gelöscht werden."
          confirmLabel="Löschen"
          onConfirm={async () => {
            try {
              await mutations.deleteFolder(dialogs.deleteFolder!);
              dialogs.closeDeleteFolder();
            } catch {
              // Fehler-Toast (z.B. folder_not_empty) kommt aus der Mutation; Dialog bleibt offen.
            }
          }}
        />
      )}
    </>
  );
}
